import { defineStore } from 'pinia';

/** A queued content-fetch task (see content.ts `getContent`) */
export interface ContentQueueElement {
  /** Position of the box in the answer flow */
  step?: number;
  /** Unique bot ID the content belongs to */
  botId?: string;
  /** Content template name */
  template?: string;
  /** Intent the content belongs to */
  intent?: string;
  /** Box ID to fetch; combined with `channel` it forms the queue key */
  box?: string;
  /** Channel ID, or undefined for the default channel */
  channel?: string;
  /** Whether follow-up boxes should also be queued */
  preloading?: boolean;
}

/** State of the contentQueue module (worker queue for content fetching) */
export interface ContentQueueState {
  /** cache for syncing states */
  contentQueue: {
    /** Whether the queue is currently processing */
    start: boolean;
    /** Number of currently active workers */
    worker: number;
    /** Maximum number of parallel workers */
    maxWorker: number;
    /** Pending tasks keyed by `box + channel` */
    queue: Record<string, ContentQueueElement>;
  };
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/contentQueue.ts` (namespace
 * `contentQueue`). In-memory only — no persistence in the legacy module either.
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Legacy action `resetQueue` merely committed the mutation `resetQueue` of the same name —
 *   merged into a single action with the mutation's body.
 * - No getter/action/mutation name collisions otherwise; no cross-namespace (rootGetters/dispatch)
 *   access exists in the legacy module.
 */
export const useContentQueueStore = defineStore('contentQueue', {
  state: (): ContentQueueState => ({
    contentQueue: {
      start: false,
      worker: 0,
      maxWorker: 8,
      queue: {},
    }, // cache for syncing states
  }),
  getters: {
    isSyncing(state): boolean {
      return state.contentQueue.start;
    },
    getQueue(state): (name: string) => ContentQueueElement | undefined {
      return (name: string) => state.contentQueue.queue[name];
    },
    isEmpty(state): boolean {
      return (
        Object.keys(state.contentQueue.queue).length === 0 &&
        state.contentQueue.start &&
        state.contentQueue.worker === 0
      );
    },
    getQueues(state): string[] {
      return Object.keys(state.contentQueue.queue);
    },
    workerSlotsAvailable(state): boolean {
      return (
        state.contentQueue.worker < state.contentQueue.maxWorker &&
        Object.keys(state.contentQueue.queue).length > 0
      );
    },
  },
  actions: {
    /**
     * Converted from legacy mutation `addQueue` (no matching legacy action existed).
     */
    addQueue({ element }: { element: ContentQueueElement }) {
      if (
        element &&
        element.box &&
        (this.contentQueue.queue as any) !== false &&
        !this.contentQueue.queue[element.box + element.channel]
      ) {
        this.contentQueue.queue = {
          ...this.contentQueue.queue,
          [element.box + element.channel]: element,
        };
      } else {
        this.contentQueue.queue = {
          [element.box + (element.channel as any)]: element,
        };
      }
    },
    /**
     * Merged from legacy action `resetQueue` (which only committed the mutation of the same
     * name) + legacy mutation `resetQueue`.
     */
    resetQueue() {
      this.contentQueue.start = false;
      this.contentQueue.queue = {};
      this.contentQueue.worker = 0;
    },
    /**
     * Converted from legacy mutation `removeQueue` (no matching legacy action existed).
     */
    removeQueue(name: string) {
      delete this.contentQueue.queue[name];
      this.contentQueue.queue = { ...this.contentQueue.queue };
    },
    /**
     * Converted from legacy mutation `removeWorker` (no matching legacy action existed).
     */
    removeWorker() {
      if (this.contentQueue.worker > 0) {
        this.contentQueue.worker--;
      }
    },
    /**
     * Converted from legacy mutation `addWorker` (no matching legacy action existed).
     */
    addWorker() {
      this.contentQueue.worker++;
    },
    /**
     * Converted from legacy mutation `finish` (no matching legacy action existed).
     */
    finish() {
      this.contentQueue.start = false;
    },
    /**
     * Converted from legacy mutation `start` (no matching legacy action existed).
     */
    start() {
      this.contentQueue.start = true;
    },
  },
});
